import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {ChevronUp, ChevronDown, Minus} from 'lucide-react';
import {selectTests, selectSubscription} from '../../slices/testSlice';
import {useDashboardData} from '../../hooks/useDashboardData';
import {useAuth} from '../../hooks/useAuth';
import {
    getLatestBiomarker,
    formatTestDate,
    parseReferenceRange,
    calculateTrendChange,
    cleanBiomarkerName,
    getAllUniqueBiomarkers,
    getBiomarkerByName,
} from '../../utils/biomarkerUtils';
import {getCustomBiomarkerConfig, isValueOnlyDisplay} from '../../utils/customBiomarkers';
import {BiomarkerStatus} from '../../types/biomarker';
import SectionContainer from '../common/SectionContainer';
import BiomarkerSelect from '../common/BiomarkerSelect';
import DashboardButton from '../common/DashboardButton';
import ProgressBar from '../charts/ProgressBar';
import TestingHistory from '../charts/TestingHistory';
import {getTestEducation} from '../../data/testEducation';
import {BIOMARKER_STATUS_COLORS, BIOMARKER_VALUE_COLOR} from '../../constants/colors';
import {getTestCategory} from '../../utils/healthCategories';

/**
 * Helper function to format text with bold titles (lines ending with ':')
 */
const formatTextWithBoldTitles = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    return lines.map((line, index) => {
        // Check if line ends with ':' (title line)
        const colonMatch = line.match(/^(.+:)(.*)$/);
        if (colonMatch) {
            const [, title, rest] = colonMatch;
            return (
                <React.Fragment key={index}>
                    <strong>{title}</strong>
                    {rest}
                    {index < lines.length - 1 && '\n'}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && '\n'}
            </React.Fragment>
        );
    });
};

interface LabResultsProps {
    selectedBiomarkerName?: string | null;
    onBiomarkerChange?: (name: string | null) => void;
    onCategoryChange?: (category: string) => void;
}

const LabResults: React.FC<LabResultsProps> = ({selectedBiomarkerName, onBiomarkerChange, onCategoryChange}) => {
    const [showInsights, setShowInsights] = useState(false);
    const testsState = useSelector(selectTests);
    const subscription = useSelector(selectSubscription);
    const {user} = useAuth();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: tests} = useDashboardData();
    const isLoading = testsState.loading;
    const isError = false; // Redux doesn't currently track errors

    // Get all unique biomarkers for the dropdown
    const allBiomarkers = React.useMemo(() => getAllUniqueBiomarkers(tests, gender), [tests, gender]);

    // State for selected biomarker - initialize with the latest biomarker
    const defaultBiomarker = React.useMemo(() => getLatestBiomarker(tests, gender), [tests, gender]);
    const [selectedBiomarker, setSelectedBiomarker] = React.useState(defaultBiomarker);

    // Update selected biomarker when default changes (e.g., new data loaded)
    React.useEffect(() => {
        if (defaultBiomarker && !selectedBiomarker) {
            setSelectedBiomarker(defaultBiomarker);
        }
    }, [defaultBiomarker, selectedBiomarker]);

    // Sync with external selection from parent (e.g., from LabTrends click)
    // Use a ref to track the last synced name to prevent infinite loops
    const lastSyncedNameRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (selectedBiomarkerName && tests.length > 0 && selectedBiomarkerName !== lastSyncedNameRef.current) {
            const biomarker = getBiomarkerByName(tests, selectedBiomarkerName, gender);
            if (biomarker) {
                lastSyncedNameRef.current = selectedBiomarkerName;
                setSelectedBiomarker(biomarker);
            }
        }
    }, [selectedBiomarkerName, tests, gender]);

    // Handler for internal dropdown selection
    const handleBiomarkerSelect = (biomarker: typeof selectedBiomarker) => {
        setSelectedBiomarker(biomarker);
        if (biomarker) {
            const cleanedName = cleanBiomarkerName(biomarker.testName);
            lastSyncedNameRef.current = cleanedName;
            onBiomarkerChange?.(cleanedName);

            // Also update the category to match the selected biomarker
            const category = getTestCategory(biomarker.testName);
            if (category !== 'other') {
                onCategoryChange?.(category);
            }
        }
    };

    // Use selected biomarker for display
    const latestBiomarker = selectedBiomarker;

    // Helper to get status color class
    const getStatusColorClass = (status: BiomarkerStatus) => {
        if (status === 'Normal' || status === 'Optimal') return 'text-mld-status-normal';
        if (status === 'High') return 'text-mld-status-high';
        if (status === 'Low') return 'text-mld-status-low';
        if (status === 'Borderline') return 'text-mld-status-borderline';
        if (status === 'Elevated') return 'text-mld-status-elevated';
        return 'text-gray-500';
    };

    // Get test education information
    const testEducation = latestBiomarker ? getTestEducation(latestBiomarker.testName) : null;

    // Generate trend data using the same logic as ResultsView
    const trendData = React.useMemo(() => {
        if (!latestBiomarker) return [];

        if (subscription && tests && tests.length > 0) {
            // For subscribers, get real historical data
            const allResults: any[] = [];

            // Collect all results for this test name across all patient tests
            tests.forEach((test: any) => {
                if (test.results) {
                    test.results.forEach((result: any) => {
                        if (result.test_name === latestBiomarker.testName) {
                            allResults.push({
                                ...result,
                                amendedDate: test.amendedDate,
                                createdDate: test.createdDate,
                            });
                        }
                    });
                }
            });

            // Require at least 2 data points to show a meaningful trend
            if (allResults.length > 1) {
                const sortedResults = allResults.sort((a, b) => {
                    // Sort by result statusDate first (most accurate for biomarker timing),
                    // fallback to test amendedDate, then createdDate
                    const aDate = new Date(a.statusDate || a.amendedDate || a.createdDate).getTime();
                    const bDate = new Date(b.statusDate || b.amendedDate || b.createdDate).getTime();
                    return aDate - bDate;
                });

                // Exclude the most recent result from historical data since it will be shown as "Current"
                const historicalResults = sortedResults.slice(0, -1); // All except the last (most recent)
                const currentResult = sortedResults[sortedResults.length - 1]; // Most recent

                const realHistoricalData = [
                    ...historicalResults.map((result) => ({
                        name: new Date(result.statusDate || result.createdDate).toLocaleDateString('en-US', {
                            month: 'short',
                            year: '2-digit',
                        }),
                        value: parseFloat(result.result) || 0,
                        label: new Date(result.statusDate || result.createdDate).toLocaleDateString('en-US', {
                            month: 'short',
                            year: '2-digit',
                        }),
                    })),
                    // Add current value
                    {
                        name: 'Current',
                        value: parseFloat(currentResult.result) || 0,
                        label: 'Current',
                    },
                ];

                return realHistoricalData;
            }

            // If we have exactly 1 result, don't show chart - insufficient trend data
            if (allResults.length === 1) {
                return [];
            }
        }

        // Fall back to mock data for non-subscribers or when no test data
        const baseValue = parseFloat(latestBiomarker.result) || 0;
        if (isNaN(baseValue)) return [];

        const roundToOne = (num: number) => Math.round(num * 10) / 10;

        return [
            {name: "Dec '24", value: roundToOne(baseValue * 0.85)},
            {name: "Feb '25", value: roundToOne(baseValue * 0.92)},
            {name: "Jun '25", value: roundToOne(baseValue * 0.88)},
            {name: 'Current', value: baseValue},
        ];
    }, [latestBiomarker, subscription, tests]);

    // Check if this is a binary test (positive/negative) - handle typo 'Postive'
    const isBinaryTest =
        latestBiomarker?.result?.toLowerCase() === 'positive' ||
        latestBiomarker?.result?.toLowerCase() === 'postive' || // Handle typo
        latestBiomarker?.result?.toLowerCase() === 'negative' ||
        latestBiomarker?.status?.toLowerCase() === 'positive' ||
        latestBiomarker?.status?.toLowerCase() === 'postive' || // Handle typo
        latestBiomarker?.status?.toLowerCase() === 'negative';

    // Check if we should show testing history
    // For binary tests: always hide testing history
    // For other tests: show if we have any data (even just one point)
    const hasTestingHistory = !isBinaryTest && trendData && trendData.length > 0;

    // Check for custom biomarker configuration (with gender for gender-specific biomarkers)
    const customConfig = latestBiomarker ? getCustomBiomarkerConfig(latestBiomarker.testName, gender) : null;
    const isValueOnly = latestBiomarker ? isValueOnlyDisplay(latestBiomarker.testName, 'dashboard') : false;

    // Calculate reference range for thresholds
    const refRange = latestBiomarker?.refRange ? parseReferenceRange(latestBiomarker.refRange) : null;
    const thresholds = React.useMemo(
        () => (refRange ? {low: refRange.min, high: refRange.max} : {low: 0, high: 100}),
        [refRange]
    );

    // Build progress bar sections based on custom config or default
    const progressBarSections = React.useMemo(() => {
        if (!customConfig || customConfig.zones.length === 0) {
            // Default 3-zone layout
            return [
                {width: 33.33, color: BIOMARKER_STATUS_COLORS.LOW},
                {width: 33.33, color: BIOMARKER_STATUS_COLORS.NORMAL},
                {width: 33.33, color: BIOMARKER_STATUS_COLORS.HIGH},
            ];
        }

        // Custom zones from config
        return customConfig.zones.map((zone) => ({
            width: zone.percentage,
            color: zone.color,
        }));
    }, [customConfig]);

    // Calculate optimal zone info (label, position, min/max values)
    const optimalZoneInfo = React.useMemo(() => {
        if (!customConfig || customConfig.zones.length === 0) {
            // Default: optimal zone is middle (33.33-66.67%)
            return {
                label: 'Optimal',
                startPercent: 33.33,
                endPercent: 66.67,
                minValue: refRange?.min ?? thresholds.low,
                maxValue: refRange?.max ?? thresholds.high,
            };
        }

        // Find the zone with "Normal" or "Optimal" status (the green zone)
        let cumulativePercent = 0;
        const optimalZone = customConfig.zones.find((zone) => zone.status === 'Normal' || zone.status === 'Optimal');

        if (!optimalZone) {
            // Fallback to default
            return {
                label: 'Optimal',
                startPercent: 33.33,
                endPercent: 66.67,
                minValue: refRange?.min ?? thresholds.low,
                maxValue: refRange?.max ?? thresholds.high,
            };
        }

        // Calculate position for this zone
        for (const zone of customConfig.zones) {
            if (zone === optimalZone) break;
            cumulativePercent += zone.percentage;
        }

        return {
            label: optimalZone.label, // Use the zone's label (e.g., "In Range" for Cortisol)
            startPercent: cumulativePercent,
            endPercent: cumulativePercent + optimalZone.percentage,
            minValue: optimalZone.min,
            maxValue: optimalZone.max,
        };
    }, [customConfig, refRange, thresholds]);

    // Show loading state
    if (isLoading) {
        return (
            <SectionContainer title="Lab Results">
                <div className="bg-white rounded-lg border border-mld-panel-border p-6 mb-4">
                    <div className="text-center py-8 text-mld-card-text">Loading latest lab results...</div>
                </div>
            </SectionContainer>
        );
    }

    // Show error state
    if (isError || !latestBiomarker) {
        return (
            <SectionContainer title="Lab Results">
                <div className="bg-white rounded-lg border border-mld-panel-border p-6 mb-4">
                    <div className="text-center py-8 text-mld-card-text">
                        {isError ? 'Unable to load lab results' : 'No lab results available'}
                    </div>
                </div>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer
            title="Lab Results"
            headerExtra={
                allBiomarkers.length > 1 ? (
                    <BiomarkerSelect
                        biomarkers={allBiomarkers}
                        selectedBiomarker={selectedBiomarker}
                        onSelect={handleBiomarkerSelect}
                    />
                ) : null
            }>
            {/* Main content area */}
            <div className="bg-white rounded-lg border border-mld-panel-border p-4 mb-4">
                <div
                    className={`grid gap-6 lg:gap-8 ${
                        hasTestingHistory ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
                    }`}>
                    {/* Left side - Lab Result */}
                    <div className="relative">
                        <div className="mb-4">
                            {/* Header with title and value */}
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3
                                        className="text-[28px] font-normal text-mld-card-text mb-1"
                                        style={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            lineHeight: 'auto',
                                            letterSpacing: '0%',
                                        }}>
                                        {cleanBiomarkerName(latestBiomarker.testName)}
                                    </h3>
                                    {!isValueOnly &&
                                        latestBiomarker.status &&
                                        latestBiomarker.status.toLowerCase() !== 'unknown' &&
                                        (() => {
                                            const trendChange = calculateTrendChange(tests, latestBiomarker.testName);

                                            return (
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`text-[23px] font-normal ${getStatusColorClass(
                                                            latestBiomarker.status
                                                        )}`}
                                                        style={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            lineHeight: 'auto',
                                                            letterSpacing: '0%',
                                                        }}>
                                                        {latestBiomarker.status}
                                                    </span>

                                                    {/* Status Icon */}
                                                    {latestBiomarker.status === 'Normal' ||
                                                    latestBiomarker.status === 'Optimal' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-normal flex items-center justify-center">
                                                            <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                                                <path
                                                                    d="M1 3L2.5 4.5L7 0.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.NORMAL}
                                                                    strokeWidth="1.2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : latestBiomarker.status === 'High' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-high flex items-center justify-center">
                                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                                <path
                                                                    d="M1 0V6.5M1 8.5V9.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.HIGH}
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : latestBiomarker.status === 'Low' ||
                                                      latestBiomarker.status === 'Elevated' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-low flex items-center justify-center">
                                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                                <path
                                                                    d="M1 0V6.5M1 8.5V9.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.LOW}
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : latestBiomarker.status === 'Borderline' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-borderline flex items-center justify-center">
                                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                                <path
                                                                    d="M1 0V6.5M1 8.5V9.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.BORDERLINE}
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-500 flex items-center justify-center">
                                                            <svg width="2" height="2" viewBox="0 0 2 2" fill="none">
                                                                <circle cx="1" cy="1" r="0.8" fill="gray" />
                                                            </svg>
                                                        </div>
                                                    )}

                                                    {/* Trend Icon (new) */}
                                                    {trendChange.changeType === 'up' ? (
                                                        <ChevronUp
                                                            className={`w-6 h-6 ${getStatusColorClass(
                                                                latestBiomarker.status
                                                            )}`}
                                                            strokeWidth={3}
                                                        />
                                                    ) : trendChange.changeType === 'down' ? (
                                                        <ChevronDown
                                                            className={`w-6 h-6 ${getStatusColorClass(
                                                                latestBiomarker.status
                                                            )}`}
                                                            strokeWidth={3}
                                                        />
                                                    ) : (
                                                        <Minus
                                                            className={`w-6 h-6 ${getStatusColorClass(
                                                                latestBiomarker.status
                                                            )}`}
                                                            strokeWidth={3}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })()}
                                </div>
                                <div className="text-right">
                                    <div
                                        className="text-[33px] font-normal"
                                        style={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            lineHeight: 'auto',
                                            letterSpacing: '0%',
                                            color: BIOMARKER_VALUE_COLOR,
                                        }}>
                                        {latestBiomarker.result}
                                    </div>
                                    {latestBiomarker.units && (
                                        <div
                                            className="text-[23px] font-normal text-mld-card-text"
                                            style={{
                                                fontFamily: 'Poppins',
                                                fontWeight: 400,
                                                lineHeight: 'auto',
                                                letterSpacing: '0%',
                                                color: '#97A6AC',
                                            }}>
                                            {latestBiomarker.units}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Dynamic progress bar based on reference range */}
                            {!isValueOnly && (
                                <div className="my-14">
                                    {isBinaryTest ? (
                                        <ProgressBar
                                            sections={[
                                                {width: 50, color: BIOMARKER_STATUS_COLORS.NORMAL}, // Green for negative
                                                {width: 50, color: BIOMARKER_STATUS_COLORS.HIGH}, // Pink for positive
                                            ]}
                                            indicatorPosition={
                                                latestBiomarker.result?.toLowerCase() === 'positive' ||
                                                latestBiomarker.result?.toLowerCase() === 'postive' ||
                                                latestBiomarker.status?.toLowerCase() === 'positive' ||
                                                latestBiomarker.status?.toLowerCase() === 'postive'
                                                    ? 75
                                                    : 25
                                            }
                                            indicatorColor={
                                                latestBiomarker.result?.toLowerCase() === 'positive' ||
                                                latestBiomarker.result?.toLowerCase() === 'postive' ||
                                                latestBiomarker.status?.toLowerCase() === 'positive' ||
                                                latestBiomarker.status?.toLowerCase() === 'postive'
                                                    ? BIOMARKER_STATUS_COLORS.HIGH
                                                    : BIOMARKER_STATUS_COLORS.NORMAL
                                            }
                                            className="w-full mb-0"
                                        />
                                    ) : (
                                        <ProgressBar
                                            sections={progressBarSections}
                                            indicatorPosition={latestBiomarker.progressBarPosition}
                                            className="w-full mb-0"
                                        />
                                    )}

                                    {/* Range labels - positioned at edges of optimal/in-range zone */}
                                    {!isBinaryTest && (
                                        <div className="relative mt-6 text-xs text-gray-500">
                                            {/* Label positioned at center of optimal zone */}
                                            <div className="relative mb-3 flex">
                                                <span
                                                    className="absolute"
                                                    style={{
                                                        left: `${optimalZoneInfo.startPercent + (optimalZoneInfo.endPercent - optimalZoneInfo.startPercent) / 2}%`,
                                                        transform: 'translateX(-50%)',
                                                    }}>
                                                    {optimalZoneInfo.label}
                                                </span>
                                            </div>
                                            <div className="absolute top-0 left-0 w-full flex">
                                                {/* Min value with dashed line */}
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        left: `calc(${optimalZoneInfo.startPercent}% + 8px)`,
                                                        transform: 'translateX(-50%)',
                                                    }}>
                                                    <div className="flex flex-col items-center">
                                                        <div
                                                            style={{
                                                                width: '2px',
                                                                height: '16px',
                                                                borderLeft: '2px dashed #9CA3AF',
                                                            }}
                                                        />
                                                        <span className="mt-2">{optimalZoneInfo.minValue}</span>
                                                    </div>
                                                </div>
                                                {/* Max value with dashed line */}
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        left: `calc(${optimalZoneInfo.endPercent}% - 8px)`,
                                                        transform: 'translateX(-50%)',
                                                    }}>
                                                    <div className="flex flex-col items-center">
                                                        <div
                                                            style={{
                                                                width: '2px',
                                                                height: '16px',
                                                                borderLeft: '2px dashed #9CA3AF',
                                                            }}
                                                        />
                                                        <span className="mt-2">{optimalZoneInfo.maxValue}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="text-right">
                                <p
                                    className="text-[15px] font-normal text-mld-card-text"
                                    style={{
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        lineHeight: 'auto',
                                        letterSpacing: '0%',
                                        color: '#97A5AD',
                                    }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="inline mr-1">
                                        <rect
                                            x="3"
                                            y="4"
                                            width="18"
                                            height="18"
                                            rx="2"
                                            ry="2"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        />
                                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                                    </svg>
                                    Last Tested {formatTestDate(latestBiomarker.statusDate)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right side - Testing History (only show if there's data) */}
                    {hasTestingHistory && (
                        <div className="lg:border-l border-mld-panel-border py-6 px-6 -mr-6 lg:-my-4">
                            <TestingHistory
                                data={trendData}
                                title="Testing History"
                                height={200}
                                showGrid={true}
                                showVerticalLines={true}
                                thresholds={thresholds}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-0">
                {testEducation && (
                    <DashboardButton
                        text={showInsights ? 'Hide Insights' : 'Result Insights'}
                        onClick={() => setShowInsights(!showInsights)}
                    />
                )}
                <div className="hidden">
                    <DashboardButton text="Related Products" />
                </div>
            </div>

            {/* Expandable Test Education Section - Clean White Card */}
            {showInsights && testEducation && (
                <div className="mt-6 bg-white p-4 lg:p-6 rounded-lg border border-mld-panel-border shadow-sm">
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 text-xs font-bold">i</span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-base font-medium text-gray-900 mb-3">{testEducation.title}</h3>

                            <div className="text-sm text-gray-700 space-y-3">
                                <p>{testEducation.description}</p>

                                {/* Always show relevant status-specific information */}
                                {testEducation.lowLevel && (
                                    <>
                                        <p>
                                            <strong>{testEducation.lowLevel.title}</strong> -{' '}
                                            {testEducation.lowLevel.description}
                                        </p>
                                        {testEducation.lowLevel.symptoms &&
                                            testEducation.lowLevel.symptoms.length > 0 && (
                                                <>
                                                    <p>
                                                        <strong>
                                                            Symptoms of low {latestBiomarker.testName.toLowerCase()} can
                                                            include:
                                                        </strong>
                                                    </p>
                                                    <ul className="list-none space-y-1 ml-4">
                                                        {testEducation.lowLevel.symptoms.map((symptom, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 mr-2 flex-shrink-0"></span>
                                                                {symptom}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}
                                    </>
                                )}

                                {testEducation.highLevel && (
                                    <>
                                        <p>
                                            <strong>{testEducation.highLevel.title}</strong> -{' '}
                                            {testEducation.highLevel.description}
                                        </p>
                                        {testEducation.highLevel.symptoms &&
                                            testEducation.highLevel.symptoms.length > 0 && (
                                                <>
                                                    <p>
                                                        <strong>
                                                            Symptoms of elevated{' '}
                                                            {latestBiomarker.testName.toLowerCase()} can include:
                                                        </strong>
                                                    </p>
                                                    <ul className="list-none space-y-1 ml-4">
                                                        {testEducation.highLevel.symptoms.map((symptom, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 mr-2 flex-shrink-0"></span>
                                                                {symptom}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}
                                    </>
                                )}

                                {testEducation.normalLevel && (
                                    <p>
                                        <strong>{testEducation.normalLevel.title}</strong> -{' '}
                                        {testEducation.normalLevel.description}
                                    </p>
                                )}

                                {/* Additional information */}
                                {testEducation.additionalInfo && (
                                    <p className="whitespace-pre-line">
                                        {formatTextWithBoldTitles(testEducation.additionalInfo)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SectionContainer>
    );
};

export default LabResults;
